import React, { SetStateAction } from "react";
import { Form, Formik, FormikProps } from "formik";
import api from "api";
import { endpoints } from "config";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";
import { Gateways } from "../../GatewaysSummary";
import { HeaderMilestoneNAProps } from "../GatewaySummary.types";
import { useOptionalGatewayChange } from "../../../../context";

/**
 * HeaderMilestoneNA component renders the NA (Not Applicable) checkbox for gateways
 * Allows users to mark entire gateway as not applicable
 */
const HeaderMilestoneNA: React.FC<HeaderMilestoneNAProps> = ({
  gateway,
  projectID,
  gatewaysID,
  gatewayTemplateID,
  gatewayIndex,
  gatewayData,
  setGatewayData,
  backgroundReloadGatewayDataToggle,
  setBackgroundReloadGatewayDataToggle,
  setExpand,
}) => {
  // Gateway change tracking for AG Grid refresh on panel close
  const gatewayChange = useOptionalGatewayChange();
  const handleChangeSuccess = () => {
    gatewayChange?.setHasChanges(true);
  };

  return (
    <Formik
      initialValues={{
        projectsID: projectID,
        gatewaysID: gatewaysID,
        gatewayNaValue: gateway.bGatewayNAValue,
      }}
      onSubmit={() => {}}
    >
      {(formik) => {
        return (
          <Form className={`HeaderNa ${gateway.bGatewayNALocked ? "NaLocked" : ""}`}>
            <div className="NAContainer">
              <input
                type="checkbox"
                id={gateway.gatewayTemplateID + "_NA"}
                className="NAInput"
                checked={gateway.bGatewayNAValue}
                disabled={gateway.bGatewayNALocked}
                onChange={(e) => {
                  handleNaChange(
                    !gateway.bGatewayNAValue,
                    formik,
                    gatewayIndex,
                    gatewayData,
                    setGatewayData,
                    gatewaysID,
                    gatewayTemplateID,
                    projectID,
                    backgroundReloadGatewayDataToggle,
                    setBackgroundReloadGatewayDataToggle,
                    setExpand,
                    handleChangeSuccess
                  );
                }}
              />

              <label className="CustomInput" htmlFor={gateway.gatewayTemplateID + "_NA"}>
                NA
              </label>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

/**
 * Handle NA checkbox change and update gateway data via API
 */
const handleNaChange = async (
  naValue: boolean,
  formik: FormikProps<any>,
  gatewayIndex: number,
  gatewayData: Gateways,
  setGatewayData: React.Dispatch<SetStateAction<Gateways | null>>,
  gatewaysID: string | number,
  gatewayTemplateID: number | string,
  projectID: number | null,
  backgroundReloadGatewayDataToggle: boolean,
  setBackgroundReloadGatewayDataToggle: React.Dispatch<SetStateAction<boolean>>,
  setExpand: React.Dispatch<SetStateAction<boolean>>,
  onChangeSuccess?: () => void
) => {
  // Collapse gateway if NA is checked
  if (naValue) {
    setExpand(false);
  }

  // Update local state
  let updatedGatewayData = { ...gatewayData };
  updatedGatewayData.gateways[gatewayIndex].bGatewayNAValue = naValue;

  // Create payload for API
  const newGatewayPayload = {
    gatewaysID: gatewaysID,
    gatewayTemplateID: gatewayTemplateID,
    projectsID: projectID,
    bGatewayNAValue: naValue,
  };

  try {
    const response = await api.post(
      endpoints.projectmanagement.gatewaysEditNaValue,
      newGatewayPayload
    );

    if (response.status === 200 && response.data && response.data.status === "1") {
      // Update state with new NA value
      setGatewayData(updatedGatewayData);

      // Trigger background reload
      setBackgroundReloadGatewayDataToggle(!backgroundReloadGatewayDataToggle);

      // Notify that gateway data has changed (for AG Grid refresh on panel close)
      onChangeSuccess?.();
    } else {
      throw new Error("Failed to update NA value");
    }
  } catch (error) {
    toast(
      <ToastMessage
        toastData={[
          {
            title: "Error",
            text: "Failed to save Gateway NA value",
          },
        ]}
        className="ToastError"
      />,
      {
        ...toastConfig,
      }
    );
  }
};

export { HeaderMilestoneNA };
